// #1294 — the start screen's settings, off the canvas.
//
// Sky Creative hold us to how the covers are presented, so nothing sits on the
// photograph except the composer card. The choices a new session needs — Job
// and Persona — live here, behind one icon in the composer's control row.
//
// A Manager also picks Chief of Staff mode here (design/2026-10-26-chief-of-
// staff-agents-panel.md T1): a checkbox, not a composer button, because the
// mode only changes what the new chat is made with.
//
// The microphone choice is here too (Adam, 14 Sep): it was a caret beside the
// mic in the composer, which read as a stray second disc.
//
// Props only: every value and every callback comes from AgentCreatePage, which
// owns the state and does all the fetching (Page → Hook → Component).
import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import type { AudioDevice } from '../../agent'
import MicrophoneSelect from './MicrophoneSelect'

export interface SessionJobOption {
  name: string
  displayName?: string
}

interface SessionOptionsDialogProps {
  open: boolean
  onClose: () => void
  disabled?: boolean

  jobs: SessionJobOption[]
  /**
   * The jobs the session will be scoped to. Empty is a state the user can
   * reach, but NOT one a session can start in: Send refuses it with an info
   * message. There is deliberately no "All jobs" — too wide a search area, so
   * the user picks consciously.
   */
  selectedJobs: string[]
  onJobsChange: (e: SelectChangeEvent<string[]>) => void

  personaNames: string[]
  persona: string
  onPersonaChange: (e: SelectChangeEvent<string>) => void
  onOpenPersonaInfo: () => void

  microphones: AudioDevice[]
  microphoneId: string | null
  onMicrophoneChange: (deviceId: string | null) => void

  /**
   * Chief of Staff mode: shown only to a Manager (goapi refuses the profile
   * for anyone else) and only when a setter is given (an alert's chat is
   * always Normal, so the page passes none there).
   */
  isManager?: boolean
  chiefOfStaff?: boolean
  onChiefOfStaffChange?: (on: boolean) => void
}

export default function SessionOptionsDialog({
  open,
  onClose,
  disabled,
  jobs,
  selectedJobs,
  onJobsChange,
  personaNames,
  persona,
  onPersonaChange,
  onOpenPersonaInfo,
  microphones,
  microphoneId,
  onMicrophoneChange,
  isManager,
  chiefOfStaff,
  onChiefOfStaffChange,
}: SessionOptionsDialogProps) {
  const noJobs = selectedJobs.length === 0
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      data-testid="agent-start-options-dialog"
    >
      <DialogTitle sx={{ fontSize: 18, fontWeight: 600 }}>Session options</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="job-select-label" shrink>Job</InputLabel>
            <Select
              labelId="job-select-label"
              multiple
              value={selectedJobs}
              onChange={onJobsChange}
              label="Job"
              displayEmpty
              notched
              renderValue={(selected) => (
                selected.length === 0
                  ? 'No job selected'
                  : selected.map(name => jobs.find(j => j.name === name)?.displayName || name).join(', ')
              )}
              disabled={disabled}
              data-testid="agent-start-job-select"
            >
              {jobs.map(j => (
                <MenuItem key={j.name} value={j.name}>
                  <Checkbox checked={selectedJobs.includes(j.name)} size="small" />
                  <ListItemText primary={j.displayName || j.name} />
                </MenuItem>
              ))}
            </Select>
            {noJobs && <FormHelperText>Pick at least one job to start a session.</FormHelperText>}
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="persona-select-label">Persona</InputLabel>
              <Select
                labelId="persona-select-label"
                value={persona}
                onChange={onPersonaChange}
                label="Persona"
                disabled={disabled}
              >
                {personaNames.map((p: string) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Blue here, on the dialog's white; it is only white when it sits
                on a cover photograph. */}
            <Tooltip title="Persona details">
              <IconButton size="small" color="info" onClick={onOpenPersonaInfo}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {isManager && onChiefOfStaffChange && (
            <Box>
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    color="info"
                    checked={Boolean(chiefOfStaff)}
                    onChange={(_e, on) => onChiefOfStaffChange(on)}
                    disabled={disabled}
                    inputProps={{ 'aria-describedby': 'chief-of-staff-help', 'data-testid': 'agent-start-options-chief-of-staff' } as React.InputHTMLAttributes<HTMLInputElement>}
                  />
                )}
                label="Chief of Staff mode"
              />
              <FormHelperText id="chief-of-staff-help" sx={{ mt: 0, ml: 4 }}>
                The agent can hand parts of the work to sub-agents and workflows, and waits for their results.
              </FormHelperText>
            </Box>
          )}

          <MicrophoneSelect microphones={microphones} microphoneId={microphoneId} onMicrophoneChange={onMicrophoneChange} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="info" sx={{ textTransform: 'none' }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}
